<script lang="ts">
	import './languageSwitcher.component.css';
	import { locale, locales } from 'svelte-i18n';
	import { fallbackLocale } from '../../../../utility-services/Translation.service';
	import { clickOutside } from '../../../../utils/Dom.utils';
	import { _ } from '../../../../utility-services/Translation.service';

	let dropDownElement: Element = $state();
	let isDropdownOpen = $state(false);
	let selectedLanguage = $state($locale);
	const HOVER_CLOSE_DELAY_MS = 180;
	let hoverCloseTimeout: ReturnType<typeof setTimeout> | undefined;

	function clearHoverCloseTimeout() {
		if (hoverCloseTimeout) {
			clearTimeout(hoverCloseTimeout);
			hoverCloseTimeout = undefined;
		}
	}

	function openDropdown() {
		clearHoverCloseTimeout();
		dropDownElement?.setAttribute('open', 'true');
		isDropdownOpen = true;
	}

	function closeDropdown() {
		clearHoverCloseTimeout();
		dropDownElement?.removeAttribute('open');
		isDropdownOpen = false;
	}

	function scheduleDropdownClose() {
		clearHoverCloseTimeout();
		hoverCloseTimeout = setTimeout(() => {
			closeDropdown();
		}, HOVER_CLOSE_DELAY_MS);
	}

	function handleChange(localeAvailable: string) {
		locale.set(localeAvailable);
		selectedLanguage = localeAvailable;
		closeDropdown();
		updateAriaLive(localeAvailable);
	}

	function closeSwitcher(node: HTMLElement) {
		clickOutside(node);
	}

	function handleClickOutside() {
		closeDropdown();
	}

	function handleMouseEnter() {
		openDropdown();
	}

	function handleMouseLeave() {
		scheduleDropdownClose();
	}

	function handleKeyDown(event: KeyboardEvent, localeAvailable: string) {
		if (event.key === 'Enter' || event.key === ' ') {
			handleChange(localeAvailable);
			event.preventDefault();
		}
	}

	let ariaLiveMessage = $state('');

	function updateAriaLive(selectedLanguage: string) {
		ariaLiveMessage = `${$_('selected_language')} : ${
			selectedLanguage.toUpperCase() === 'EN' ? $_('English') : $_('French')
		}`;
	}
</script>

<div class="block">
	<!-- Label caché pour les lecteurs d'écran -->
	<label id="dropdown-label" class="sr-only" for="languageSwitcher">{$_('selecte_language')}</label>

	<div class="vtmn-dropdown dropdown" use:closeSwitcher onclick_outside={handleClickOutside}>
		<details
			bind:this={dropDownElement}
			aria-controls="language-list"
			aria-labelledby="dropdown-label"
			onmouseenter={handleMouseEnter}
			onmouseleave={handleMouseLeave}
			ontoggle={() => (isDropdownOpen = dropDownElement?.hasAttribute('open') ?? false)}
		>
			<summary
				id="languageSwitcher"
				class="languageSwitcher_summary"
				aria-labelledby="dropdown-label languageSwitcher"
				onmouseenter={handleMouseEnter}
			>
				{#if $locales.includes($locale)}
					{$locale?.toUpperCase()}
				{:else}
					{fallbackLocale}
				{/if}
			</summary>

			<!-- svelte-ignore a11y_interactive_supports_focus -->
			<div
				class="vtmn-dropdown_items dropdownItems"
				id="language-list"
				role="listbox"
				onmouseenter={handleMouseEnter}
				onmouseleave={handleMouseLeave}
			>
				<!-- svelte-ignore a11y_no_noninteractive_element_to_interactive_role -->
				{#each $locales as localeAvailable}
					<label
						for="language"
						onclick={() => handleChange(localeAvailable)}
						onkeydown={(event) => handleKeyDown(event, localeAvailable)}
						role="option"
						aria-selected={selectedLanguage === localeAvailable}
						tabindex="0"
					>
						{localeAvailable.toUpperCase()}
					</label>
				{/each}
			</div>
		</details>
	</div>
	<!-- Élément pour annoncer les changements aux lecteurs d'écran -->
	<div aria-live="polite" class="sr-only">{ariaLiveMessage}</div>
</div>

<style src="./languageSwitcher.component.css"></style>
